import { PieChart } from '@lmc/design-system'
import { Heading } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  RotaInstanceStatus,
  type TodayMonitoring
} from 'screens/Rostering/types'
import styled from 'styled-components'
import EventCount, { getRotaInstanceStatus } from './EventCount'
import PieChartTooltip from './PieChartTooltip'

export enum StatusColors {
  DRAFT = '#999999',
  PENDING = '#9ACEFF',
  DUE = '#1890FF',
  IN_PROGRESS = '#9966FB',
  OVERDUE = '#E01E4E',
  DONE = '#37A598',
  CANCELLED = '#595959'
}

const STATUS_COLOURS: Record<RotaInstanceStatus, StatusColors> = {
  [RotaInstanceStatus.CANCELLED]: StatusColors.CANCELLED,
  [RotaInstanceStatus.DONE]: StatusColors.DONE,
  [RotaInstanceStatus.DRAFT]: StatusColors.DRAFT,
  [RotaInstanceStatus.DUE]: StatusColors.DUE,
  [RotaInstanceStatus.IN_PROGRESS]: StatusColors.IN_PROGRESS,
  [RotaInstanceStatus.OVERDUE]: StatusColors.OVERDUE,
  [RotaInstanceStatus.PENDING]: StatusColors.PENDING
}

type StatusWidgetProps = {
  todayMonitoring: TodayMonitoring
}

const StatusWidget = ({ todayMonitoring }: StatusWidgetProps) => {
  const { t } = useTranslation()

  if (!todayMonitoring.statusSummary) return null

  return (
    <Wrapper>
      <Heading level={3}>{t('STATUS')}</Heading>
      <StyledDivider />
      <ContentContainer>
        <EventsContainer>
          <LightText level={2}>{t('EVENTS')}</LightText>
          {Object.entries(todayMonitoring.statusSummary.statuses).map(
            ([key, value]) => (
              <EventCount type={key} count={value as number} />
            )
          )}
        </EventsContainer>
        <ChartContainer>
          <PieChart
            aria-label={t('STATUS')}
            centre={{
              caption: t('TOTAL_EVENTS'),
              value: todayMonitoring.statusSummary.total
            }}
            data={Object.entries(todayMonitoring.statusSummary.statuses).map(
              ([key, value]) => ({
                colour: STATUS_COLOURS[getRotaInstanceStatus(key)],
                name: key,
                value
              })
            )}
            height={220}
            legend="none"
            tooltip={({ name, value }) => (
              <PieChartTooltip type={name} value={value} />
            )}
          />
        </ChartContainer>
      </ContentContainer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
  width: 100%;
  min-width: 0;
  flex: 2;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`

const ChartContainer = styled.div`
  width: 220px;
  flex-shrink: 0;
`

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 8px;
  width: 100%;
`

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 48px;
  margin: 24px 0;
`

const LightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default StatusWidget
